import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import { CampaignClient, CampaignDTO } from '../../services/apiClient';
import useQuestionSets from '../../hooks/useQuestionSets';
import FileUpload from '../common/FileUpload';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import { X } from 'lucide-react';

interface CampaignFormProps {
  initialValues: CampaignDTO;
  onSubmit: (data: CampaignDTO) => void;
  onCancel: () => void;
}

const CampaignForm: React.FC<CampaignFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CampaignDTO>(initialValues);
  const [fileUrl, setfileUrl] = useState<string>('');
  const { questionSets } = useQuestionSets();
  const client = new CampaignClient(API_BASE_URL, axiosInstance);

  const getImageSas = async () => {
    try {
      if (formData.id) {
        var response = await client.generateCampaignImageSas(formData.id);
        if (response && response.success) {
          setfileUrl(response.data ?? '');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getImageSas();
  }, [formData.fileName]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    var response = await client.uploadCampaignImage(file?.name, file);
    if (response.success && response.data) {
      setFormData({
        ...formData,
        fileName: response.data,
      });
    }
  };

  const handleChange = (key: keyof CampaignDTO) => (value: string) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.questionSetId == "") {
      formData.questionSetId = undefined;
    }
    onSubmit(formData); // Pass the updated data back to the parent
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="mb-4">
        <FormField
          id="name"
          label="Cause"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange('name')(e.target.value)}
          placeholder="Enter Cause"
        />
        <FormField
          id="questionSetId"
          label="Question Set"
          type="dropdown"
          options={[
            { value: '', label: 'Please select a question set' },
            ...questionSets.map((m) => ({
              value: m.id ?? '',
              label: `${m.name}`,
            })),
          ]}
          value={formData.questionSetId || ''}
          onChange={(e) => handleChange('questionSetId')(e.target.value)}
        />
        <FormField
          id="debitOnly"
          label="Debit Only"
          type="checkbox"
          value={formData.debitOnly || false}
          onChange={(e) => setFormData({ ...formData, debitOnly: e.target.checked })}
        />
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mt-4 mb-4">
          {/* Preview on the left if image is present */}
          {fileUrl && formData.fileName && (
            <div className="relative flex-shrink-0 w-32 h-32">
              <img
                src={fileUrl}
                alt="Cause"
                className="w-full h-full object-cover rounded"
              />
              <button
                onClick={() => setFormData({ ...formData, fileName: '' })}
                className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                aria-label="Remove Cause Image"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* File upload - Stretches full width on desktop */}
          <div className="flex-1 w-full md:w-auto">
            <FileUpload
              id="fileName"
              placeholderText="Upload or drag and drop the background image for this cause."
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </div>
    </form>
  );
};

export default CampaignForm;
